import React, { useState } from 'react';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiPackage, FiRefreshCw, FiShare2, FiXCircle } from 'react-icons/fi';
import SharePositionsModal from './SharePositionsModal';
import { dashboardApi } from '../../api';
import { showToast } from '../../utils/toast';

interface AccountDetailsProps {
  account: any;
  positions: any[];
  onRefresh: () => void;
  isRefreshing: boolean;
  brokerageName?: string;
  brokerageLogo?: string;
  /** OANDA only: connection _id so user can close their own position */
  connectionId?: string;
  /** e.g. 'oanda' – show Close button only for OANDA */
  connectionType?: string;
}

const AccountDetails: React.FC<AccountDetailsProps> = ({
  account,
  positions,
  onRefresh,
  isRefreshing,
  brokerageName,
  brokerageLogo,
  connectionId,
  connectionType,
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<any>(null);
  const [closingKey, setClosingKey] = useState<string | null>(null);

  const canClosePosition = connectionType === 'oanda' && connectionId;
  const isForexLots = connectionType === 'genesis';
  
  // Debug logging for positions
  React.useEffect(() => {
    console.log(`📊 [AccountDetails] Received positions for ${brokerageName}:`, {
      positionsCount: positions?.length || 0,
      positions: positions,
      accountId: account?.id
    });
  }, [positions, brokerageName, account?.id]);
  
  if (!account) return null;

  // Normalize position data for SharePositionsModal (handles both snake_case and camelCase formats)
  const normalizePositionData = (position: any) => {
    // Extract symbol (handles both string and object formats)
    let symbol = '';
    if (typeof position.symbol === 'string') {
      symbol = position.symbol.trim();
    } else if (position.symbol && typeof position.symbol === 'object') {
      symbol = position.symbol.symbol || position.symbol.ticker || position.symbol.name || position.symbol.raw_symbol || '';
      symbol = String(symbol || '').trim();
    } else {
      symbol = String(position.symbol || '').trim();
    }
    
    // Normalize to snake_case format for SharePositionsModal
    const qty = parseFloat(position.qty || position.quantity || '0');
    const avgEntryPrice = parseFloat(position.avgEntryPrice || position.avg_entry_price || 0);
    const currentPrice = parseFloat(position.currentPrice || position.current_price || position.price || 0);
    const marketValue = parseFloat(position.marketValue || position.market_value || (qty * currentPrice) || 0);
    const totalCost = qty * avgEntryPrice;
    const costBasis = parseFloat(position.costBasis || position.cost_basis || totalCost || '0');
    const unrealizedPL = parseFloat(position.unrealizedPl || position.unrealized_pl || (marketValue - totalCost) || '0');
    const unrealizedPLPercent = parseFloat(position.unrealizedPlPercent || position.unrealized_plpc || position.unrealized_pl_percent || 0);
    
    // Calculate P/L percent if not provided
    const calculatedPLPercent = costBasis > 0 ? (unrealizedPL / costBasis) * 100 : 0;
    const plPercent = unrealizedPLPercent || calculatedPLPercent;
    const todayPL = parseFloat(position.todayUnrealizedPl || position.today_unrealized_pl || position.unrealized_intraday_pl || '0');
    const todayPLPercent = parseFloat(position.todayUnrealizedPlPercent || position.today_unrealized_plpc || position.unrealized_intraday_plpc || '0') * 100;
    
    const side = position.side || (qty >= 0 ? 'long' : 'short');
    const assetClass = position.asset_class || position.assetClass || 'us_equity';
    const name = position.name || (position.symbol && typeof position.symbol === 'object' ? position.symbol.description || position.symbol.name : '') || '';
    
    return {
      symbol: symbol || 'UNKNOWN',
      qty: qty,
      avg_entry_price: avgEntryPrice,
      current_price: currentPrice,
      last_price: currentPrice,
      market_value: marketValue,
      cost_basis: costBasis,
      unrealized_pl: unrealizedPL,
      unrealized_plpc: plPercent / 100, // Convert to decimal for modal
      today_unrealized_pl: todayPL,
      today_unrealized_plpc: todayPLPercent / 100, // Convert to decimal for modal
      unrealized_intraday_pl: todayPL,
      unrealized_intraday_plpc: todayPLPercent / 100,
      side: side,
      asset_class: assetClass,
      name: name,
      // Preserve original position data for reference
      original: position
    };
  };

  const handleSharePosition = (position: any) => {
    const normalizedPosition = normalizePositionData(position);
    setSelectedPosition(normalizedPosition);
    setShowShareModal(true);
  };

  const handleClosePosition = async (position: any) => {
    if (!canClosePosition || !connectionId) return;
    let symbol = '';
    if (typeof position.symbol === 'string') {
      symbol = position.symbol.trim();
    } else if (position.symbol && typeof position.symbol === 'object') {
      symbol = position.symbol.symbol || position.symbol.ticker || position.symbol.name || position.symbol.raw_symbol || '';
      symbol = String(symbol || '').trim();
    } else {
      symbol = String(position.symbol || '').trim();
    }
    const qty = parseFloat(position.qty || position.quantity || '0');
    const side = (position.side || (qty >= 0 ? 'long' : 'short')).toString().toLowerCase();
    const isLong = side === 'long' || qty > 0;
    const key = `${symbol}-${side}`;
    setClosingKey(key);
    try {
      await dashboardApi.closeMyPosition({
        connectionId,
        instrument: symbol,
        ...(isLong ? { longUnits: 'ALL' } : { shortUnits: 'ALL' }),
      });
      showToast('success', `Position ${symbol} closed`);
      onRefresh();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to close position');
    } finally {
      setClosingKey(null);
    }
  };

  const formatCurrency = (value: number | string, decimals: number = 2) => {
    const num = parseFloat(value?.toString() || '0');
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const formatPercent = (value: number | string, isDecimal: boolean = false, includeSign: boolean = true) => {
    const num = parseFloat(value?.toString() || '0');
    // If value is in decimal format (0.0103 = 1.03%), multiply by 100
    const percent = isDecimal ? num * 100 : num;
    // Handle NaN and Infinity
    if (isNaN(percent) || !isFinite(percent)) {
      return includeSign ? '+0.00%' : '0.00%';
    }
    // Only add sign if includeSign is true
    const sign = includeSign ? (percent >= 0 ? '+' : '') : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  const portfolioValue = parseFloat(account.portfolio_value || account.equity || '0');
  const cash = parseFloat(account.cash || '0');
  const equity = parseFloat(account.equity || '0');
  const buyingPower = parseFloat(account.buying_power || '0');
  const dayChange = parseFloat(account.equity || '0') - parseFloat(account.last_equity || account.equity || '0');
  const dayChangePercent = portfolioValue > 0 ? (dayChange / parseFloat(account.last_equity || account.equity || '1')) * 100 : 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-foreground flex items-center gap-3">
          {brokerageLogo && (
            <img src={brokerageLogo} alt={brokerageName || "Broker"} className="w-10 h-10 object-contain rounded-lg" />
          )}
          {brokerageName ? `${brokerageName} Account Details` : 'Account Details'}
        </h3>
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="btn-secondary flex items-center gap-2"
        >
          <FiRefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>

      {/* Account Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card-premium border-primary/30 bg-primary/5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted">Portfolio Value</h4>
            <FiDollarSign className="h-5 w-5 text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">{formatCurrency(portfolioValue)}</div>
          <div className={`text-sm flex items-center gap-1 ${
            dayChange >= 0 ? 'text-success' : 'text-danger'
          }`}>
            {dayChange >= 0 ? (
              <FiTrendingUp className="h-4 w-4" />
            ) : (
              <FiTrendingDown className="h-4 w-4" />
            )}
            <span>{formatCurrency(Math.abs(dayChange))} ({formatPercent(dayChangePercent)})</span>
          </div>
        </div>

        <div className="card-premium">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted">Cash</h4>
            <FiDollarSign className="h-5 w-5 text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground">{formatCurrency(cash)}</div>
          <div className="text-xs text-muted mt-1">Available for trading</div>
        </div>

        <div className="card-premium">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted">Buying Power</h4>
            <FiTrendingUp className="h-5 w-5 text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground">{formatCurrency(buyingPower)}</div>
          <div className="text-xs text-muted mt-1">Maximum purchase power</div>
        </div>

        <div className="card-premium">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted">Equity</h4>
            <FiDollarSign className="h-5 w-5 text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground">{formatCurrency(equity)}</div>
          <div className="text-xs text-muted mt-1">Account equity</div>
        </div>
      </div>

      {/* Account Info */}
      <div className="card-premium">
        <h4 className="text-lg font-semibold text-foreground mb-4">Account Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-muted">Account ID:</span>
            <div className="font-medium text-foreground">{account.id || 'N/A'}</div>
          </div>
          <div>
            <span className="text-sm text-muted">Account Status:</span>
            <div className="font-medium text-foreground capitalize">{account.status || 'ACTIVE'}</div>
          </div>
          <div>
            <span className="text-sm text-muted">Trading Blocked:</span>
            <div className={`font-medium ${account.trading_blocked ? 'text-danger' : 'text-success'}`}>
              {account.trading_blocked ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <span className="text-sm text-muted">Pattern Day Trader:</span>
            <div className={`font-medium ${account.pattern_day_trader ? 'text-warning' : 'text-foreground'}`}>
              {account.pattern_day_trader ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <span className="text-sm text-muted">Portfolio Value:</span>
            <div className="font-medium text-foreground">{formatCurrency(account.portfolio_value || account.equity)}</div>
          </div>
          <div>
            <span className="text-sm text-muted">Long Market Value:</span>
            <div className="font-medium text-foreground">{formatCurrency(account.long_market_value || '0')}</div>
          </div>
        </div>
      </div>

      {/* Positions */}
      <div className="card-premium">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-foreground">My Positions</h4>
          <span className="text-sm text-muted">{positions?.length || 0} positions</span>
        </div>
        {positions && positions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted">Symbol</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-muted">
                    {isForexLots ? 'Lot' : 'Quantity'}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-muted">Market Value</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-muted">Last Price</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-muted">Avg Price</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-muted">Total Cost</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-muted">Open P&L</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-muted">Side</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-muted">Product Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-muted">Name</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((position: any, index: number) => {
                    // Handle both snake_case and camelCase formats
                    // Symbol can be a string or an object, extract the actual symbol string
                    let symbol = '';
                    try {
                      // First, check if position.symbol exists
                      if (!position.symbol) {
                        console.warn('Position missing symbol field:', position);
                        symbol = 'UNKNOWN';
                      } else if (typeof position.symbol === 'string') {
                        // Already a string, use directly
                        symbol = position.symbol.trim();
                      } else if (position.symbol && typeof position.symbol === 'object') {
                        // Symbol is an object, extract the actual symbol string
                        symbol = position.symbol.symbol || position.symbol.ticker || position.symbol.name || position.symbol.raw_symbol || '';
                        symbol = String(symbol || '').trim();
                      } else {
                        // Fallback: try to convert to string
                        symbol = String(position.symbol).trim();
                      }
                      
                      // Final validation: ensure it's not an object representation
                      if (!symbol || symbol === '[object Object]' || symbol === 'null' || symbol === 'undefined') {
                        // Try alternative extraction paths
                        if (position.symbol && typeof position.symbol === 'object') {
                          symbol = position.symbol.symbol || position.symbol.ticker || position.symbol.name || position.symbol.raw_symbol || '';
                          symbol = String(symbol || '').trim();
                        }
                        // If still invalid, log and use fallback
                        if (!symbol || symbol === '[object Object]') {
                          console.error('Could not extract symbol from position:', {
                            positionSymbol: position.symbol,
                            positionSymbolType: typeof position.symbol,
                            fullPosition: JSON.stringify(position, null, 2).substring(0, 500)
                          });
                          symbol = 'UNKNOWN';
                        }
                      }
                    } catch (e) {
                      console.error('Error extracting symbol:', e, position);
                      symbol = 'UNKNOWN';
                    }
                    
                    // Final safety check: ensure symbol is always a string
                    symbol = String(symbol || 'UNKNOWN').trim();
                    
                    const qty = parseFloat(position.qty || position.quantity || position.lot || '0');
                    const lot = parseFloat(position.lot ?? position.qty ?? position.quantity ?? '0');
                    const size = isForexLots || String(position.productType || '').toLowerCase() === 'forex' ? lot : qty;
                    const avgEntryPrice = parseFloat(position.avgEntryPrice || position.avg_entry_price || 0);
                    const lastPrice = parseFloat(position.currentPrice || position.current_price || position.price || 0);
                    const marketValue = parseFloat(position.marketValue || position.market_value || (size * lastPrice) || 0);
                    const totalCost = size * avgEntryPrice;
                    const unrealizedPL = parseFloat(position.unrealizedPl || position.unrealized_pl || (marketValue - totalCost) || '0');
                    
                    // Determine product type (Stock or Option based on symbol pattern)
                    let productType = position.productType || 'Stock';
                    if (productType === 'Stock' && symbol) {
                      const symbolStr = String(symbol).toUpperCase();
                      // Options typically have $, spaces, dates, strike prices
                      if (symbolStr.includes('$') || symbolStr.includes(' C ') || symbolStr.includes(' P ') || 
                          /^\w+\s+\$\d+/.test(symbolStr) || /\d{2}\s+\w{3}\s+\d{4}/.test(symbolStr)) {
                        productType = 'Option';
                      }
                    }
                    
                    // Get name from position data or symbol object
                    let name = position.name || '-';
                    if (name === null || name === undefined) {
                      if (position.symbol && typeof position.symbol === 'object') {
                        name = position.symbol.description || position.symbol.name || '-';
                      } else {
                        name = '-';
                      }
                    }
                    const side = position.side || 'Long';
                    
                    return (
                      <tr key={position.asset_id || position.id || index} className="border-b border-border hover:bg-surface">
                        <td className="py-3 px-4 font-medium text-foreground">
                          {String(symbol || 'UNKNOWN')}
                        </td>
                        <td className="py-3 px-4 text-right text-foreground">
                          {(isForexLots || String(productType).toLowerCase() === 'forex')
                            ? lot.toFixed(2)
                            : qty.toFixed(0)}
                        </td>
                        <td className="py-3 px-4 text-right text-foreground">{formatCurrency(marketValue)}</td>
                        <td className="py-3 px-4 text-right text-foreground">{formatCurrency(lastPrice)}</td>
                        <td className="py-3 px-4 text-right text-foreground">{formatCurrency(avgEntryPrice, 3)}</td>
                        <td className="py-3 px-4 text-right text-foreground">{formatCurrency(totalCost)}</td>
                        <td className={`py-3 px-4 text-right font-medium ${
                          unrealizedPL >= 0 ? 'text-success' : 'text-danger'
                        }`}>
                          {unrealizedPL >= 0 ? '+' : ''}{formatCurrency(unrealizedPL)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded ${
                            side.toLowerCase() === 'long' ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'
                          }`}>
                            {side}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-muted text-sm">{productType}</td>
                        <td className="py-3 px-4 text-left text-muted text-sm">{name}</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {canClosePosition && (
                              <button
                                onClick={() => handleClosePosition(position)}
                                disabled={!!closingKey}
                                className="p-2 hover:bg-danger/10 rounded-lg transition-colors text-danger disabled:opacity-50"
                                title="Close this position"
                              >
                                <FiXCircle className="h-4 w-4" />
                              </button>
                            )}
                            {/* <button
                              onClick={() => handleSharePosition(position)}
                              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-primary hover:text-primary-variant"
                              title="Share this position"
                            >
                              <FiShare2 className="h-4 w-4" />
                            </button> */}
                          </div>
                          {canClosePosition && (() => {
                            const rowSide = (position.side || (qty >= 0 ? 'long' : 'short')).toString().toLowerCase();
                            const rowKey = `${String(symbol || '').trim()}-${rowSide}`;
                            return closingKey === rowKey ? <span className="text-xs text-muted block mt-1">Closing...</span> : null;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FiPackage className="h-12 w-12 text-muted mx-auto mb-3" />
            <p className="text-muted">No open positions</p>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && selectedPosition && (
        <SharePositionsModal
          account={account}
          position={selectedPosition}
          onClose={() => {
            setShowShareModal(false);
            setSelectedPosition(null);
          }}
        />
      )}
    </div>
  );
};

export default AccountDetails;

